import { useState } from 'react';
import { 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  Clock, 
  RefreshCw,
  Filter
} from 'lucide-react';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionPage } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

interface SmsLog {
  id: number;
  recipient: string;
  message: string;
  status: 'pending' | 'sent' | 'failed' | 'dispatched';
  gateway: string;
  message_id: string;
  error_message: string;
  sms_count: number;
  cost: number;
  target: string;
  target_id: number;
  sent_at: string;
  created_at: string;
  user: {
    name: string;
  };
}

interface Statistics {
  total: number;
  sent: number;
  failed: number;
  pending: number;
  total_sms_units: number;
  total_cost: number;
}

export default function SmsLogsPage() {
  usePermissions();
  const [filters, setFilters] = useState({
    status: '',
    recipient: '',
    message: '',
    date_from: '',
    date_to: '',
    period: '',
    per_page: 25,
  });
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  
  const queryClient = useQueryClient();

  // Fetch SMS logs
  const { data: logsData, isLoading, isError: isLogsError, error: logsError } = useQuery({
    queryKey: ['smsLogs', filters, page],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, String(value));
        });
        params.append('page', page.toString());
        
        const response = await api.get(`/sms-logs?${params}`);
        console.log('SMS Logs Response:', response.data);
        return response.data.data; // This is the paginated object
      } catch (error) {
        console.error('SMS Logs Error:', error);
        throw error;
      }
    },
    retry: 1,
  });

  // Fetch statistics
  const { data: statsData, isError: isStatsError, error: statsError } = useQuery({
    queryKey: ['smsStats', filters.period],
    queryFn: async () => {
      try {
        const response = await api.get(`/sms-logs/statistics?period=${filters.period || 'month'}`);
        console.log('SMS Stats Response:', response.data);
        return response.data.data;
      } catch (error) {
        console.error('SMS Stats Error:', error);
        throw error;
      }
    },
    retry: 1,
  });

  // Retry SMS mutation
  const retryMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post(`/sms-logs/${id}/retry`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('SMS retried successfully');
      queryClient.invalidateQueries({ queryKey: ['smsLogs'] });
      queryClient.invalidateQueries({ queryKey: ['smsStats'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to retry SMS');
    },
  });

  const getStatusBadge = (status: string) => {
    const config = {
      sent: { label: 'Sent', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: XCircle },
      pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      dispatched: { label: 'Dispatched', color: 'bg-blue-100 text-blue-800', icon: MessageSquare },
    };

    const { label, color, icon: Icon } = config[status as keyof typeof config] || config.pending;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        <Icon size={12} className="mr-1" />
        {label}
      </span>
    );
  };

  const handleRetry = (id: number) => {
    if (window.confirm('Are you sure you want to retry sending this SMS?')) {
      retryMutation.mutate(id);
    }
  };

  // Debug logging
  if (isStatsError) {
    console.error('Stats error:', statsError);
  }
  if (isLogsError) {
    console.error('Logs error:', logsError);
  }

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_SMS_HISTORY}>
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <MessageSquare size={32} className="mr-3 text-blue-600" />
            SMS Logs
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            View all sent SMS messages and statistics
          </p>
        </div>
      </div>

      {/* Error Display */}
      {(isStatsError || isLogsError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <XCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
              <p className="text-sm text-red-700 mt-1">
                {statsError?.message || logsError?.message || 'Failed to load SMS data. Please try again.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SMS History Content */}
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-gray-700 hover:text-gray-900"
            >
              <Filter size={20} className="mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters({ ...filters, period: 'today' })}
                className={`px-3 py-1 rounded text-sm ${filters.period === 'today' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
              >
                Today
              </button>
              <button
                onClick={() => setFilters({ ...filters, period: 'this_month' })}
                className={`px-3 py-1 rounded text-sm ${filters.period === 'this_month' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
              >
                This Month
              </button>
              <button
                onClick={() => setFilters({ ...filters, period: '' })}
                className={`px-3 py-1 rounded text-sm ${!filters.period ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
              >
                All Time
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t">
              <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      >
                        <option value="">All Statuses</option>
                        <option value="sent">Sent</option>
                        <option value="failed">Failed</option>
                        <option value="pending">Pending</option>
                        <option value="dispatched">Dispatched</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Recipient</label>
                      <input
                        type="text"
                        value={filters.recipient}
                        onChange={(e) => setFilters({ ...filters, recipient: e.target.value })}
                        placeholder="Search by phone number"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                      <input
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                      <input
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* SMS Logs Table */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="responsive-table min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message Content</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">SMS Units</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {isLoading ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-12 text-center">
                            <div className="flex items-center justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                              <span className="ml-3 text-gray-600">Loading SMS logs...</span>
                            </div>
                          </td>
                        </tr>
                      ) : isLogsError ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-12 text-center text-red-600">
                            <XCircle size={48} className="mx-auto mb-3" />
                            <p>Failed to load SMS logs</p>
                            <p className="text-sm text-gray-600 mt-2">{logsError?.message || 'Please try again'}</p>
                          </td>
                        </tr>
                      ) : !logsData || !logsData.data || logsData.data.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                            <MessageSquare size={48} className="mx-auto mb-3 text-gray-400" />
                            <p>No SMS logs found</p>
                            <p className="text-sm text-gray-600 mt-2">SMS messages will appear here once sent</p>
                          </td>
                        </tr>
                      ) : (
                        logsData.data.map((log: SmsLog) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td data-label="Recipient" className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {log.recipient}
                            </td>
                            <td data-label="Message Content" className="px-6 py-4 text-sm text-gray-700 max-w-md">
                              <div className="truncate" title={log.message}>
                                {log.message.substring(0, 100)}
                                {log.message.length > 100 && '...'}
                              </div>
                              {log.status === 'failed' && log.error_message && (
                                <p className="text-xs text-red-600 mt-1">{log.error_message}</p>
                              )}
                            </td>
                            <td data-label="Target" className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {log.target ? (
                                <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">
                                  {log.target}
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td data-label="Status" className="px-6 py-4 whitespace-nowrap text-center">
                              {getStatusBadge(log.status)}
                            </td>
                            <td data-label="SMS Units" className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                              {log.sms_count}
                            </td>
                            <td data-label="Date" className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {new Date(log.created_at).toLocaleString()}
                            </td>
                            <td data-label="Actions" className="px-6 py-4 whitespace-nowrap text-center text-sm">
                              {log.status === 'failed' && (
                                <button
                                  onClick={() => handleRetry(log.id)}
                                  disabled={retryMutation.isPending}
                                  className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                  title="Retry sending"
                                >
                                  <RefreshCw size={16} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {logsData && logsData.last_page > 1 && (
                  <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
                    <div className="text-sm text-gray-700">
                      Showing {logsData.from} to {logsData.to} of {logsData.total} results
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-3 py-1 border rounded disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1">
                        Page {page} of {logsData.last_page}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(logsData.last_page, p + 1))}
                        disabled={page >= logsData.last_page}
                        className="px-3 py-1 border rounded disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
      </div>
    </div>
    </PermissionPage>
  );
}
